/**
 * The emoji a sport can be marked with, curated and tagged.
 *
 * Deliberately *not* `"use client"`. Nothing here uses a hook or a handler — the
 * catalogue is data, the lookups are pure, and `SportGlyph` is a span — and the
 * directive turned every export into a client reference, so a server component
 * calling `sportEmoji()` failed with "attempted to call it from the server". A module
 * takes the directive when it needs the client, not when its callers happen to.
 *
 * Emoji rather than line icons because only emoji actually *have* the sports:
 * a general icon set has no cricket bat, no horse-and-rider and no kabaddi, so
 * every sport ended up wearing the same trophy. Emoji are characters, not
 * components, so the catalogue itself costs nothing to ship — the font is the
 * only payload, loaded once in the root layout.
 *
 * `sports` is both the search index and the visible tag: typing "cricket"
 * should land on the bat, and the chosen glyph should say what it means rather
 * than leave the operator guessing at 16px.
 */

export type SportEmoji = {
  id: string;
  glyph: string;
  /** The sports this stands for. Searched, and the first one is shown as a tag. */
  sports: string[];
  /** Extra words worth matching that are not sport names. */
  keywords?: string[];
};

export const SPORT_EMOJI: SportEmoji[] = [
  // --- ball sports ---
  { id: "cricket", glyph: "🏏", sports: ["cricket"], keywords: ["bat", "wicket"] },
  { id: "football", glyph: "⚽", sports: ["football", "soccer"], keywords: ["ball"] },
  { id: "american-football", glyph: "🏈", sports: ["american football", "nfl"] },
  { id: "rugby", glyph: "🏉", sports: ["rugby", "rugby union", "rugby league"] },
  { id: "basketball", glyph: "🏀", sports: ["basketball"], keywords: ["hoop"] },
  { id: "volleyball", glyph: "🏐", sports: ["volleyball", "beach volleyball"] },
  { id: "tennis", glyph: "🎾", sports: ["tennis"], keywords: ["racket"] },
  { id: "badminton", glyph: "🏸", sports: ["badminton"], keywords: ["shuttlecock"] },
  { id: "table-tennis", glyph: "🏓", sports: ["table tennis", "ping pong"] },
  { id: "baseball", glyph: "⚾", sports: ["baseball", "softball"] },
  { id: "golf", glyph: "⛳", sports: ["golf"], keywords: ["hole", "flag"] },
  { id: "ice-hockey", glyph: "🏒", sports: ["ice hockey"], keywords: ["stick", "puck"] },
  { id: "field-hockey", glyph: "🏑", sports: ["field hockey", "hockey"] },
  { id: "lacrosse", glyph: "🥍", sports: ["lacrosse"] },
  { id: "handball", glyph: "🤾", sports: ["handball", "kabaddi"], keywords: ["throw", "raid"] },
  { id: "bowling", glyph: "🎳", sports: ["bowling", "ten pin"] },
  { id: "pool", glyph: "🎱", sports: ["pool", "snooker", "billiards"] },
  { id: "darts", glyph: "🎯", sports: ["darts", "archery", "shooting"], keywords: ["target"] },
  { id: "frisbee", glyph: "🥏", sports: ["ultimate", "disc golf", "frisbee"] },

  // --- combat ---
  { id: "boxing", glyph: "🥊", sports: ["boxing", "mma", "kickboxing"], keywords: ["glove"] },
  {
    id: "martial-arts",
    glyph: "🥋",
    sports: ["judo", "karate", "taekwondo", "wrestling", "kabaddi"],
  },
  { id: "fencing", glyph: "🤺", sports: ["fencing"], keywords: ["sword", "epee"] },

  // --- racing and endurance ---
  { id: "horse-racing", glyph: "🏇", sports: ["horse racing", "racing"], keywords: ["jockey"] },
  { id: "equestrian", glyph: "🐎", sports: ["equestrian", "dressage", "horse racing"] },
  { id: "cycling", glyph: "🚴", sports: ["cycling", "road racing"], keywords: ["bike", "tour"] },
  { id: "mountain-biking", glyph: "🚵", sports: ["mountain biking", "cyclo cross", "bmx"] },
  { id: "athletics", glyph: "🏃", sports: ["athletics", "running", "marathon"], keywords: ["track"] },
  { id: "motorsport", glyph: "🏎️", sports: ["motorsport", "formula 1", "rallying"] },
  { id: "motorcycle", glyph: "🏍️", sports: ["motorcycle racing", "motogp", "speedway"] },
  { id: "chequered-flag", glyph: "🏁", sports: ["racing"], keywords: ["finish", "lap"] },
  { id: "roller-skating", glyph: "🛼", sports: ["roller derby", "speed skating"] },
  { id: "skateboarding", glyph: "🛹", sports: ["skateboarding"] },

  // --- water ---
  { id: "swimming", glyph: "🏊", sports: ["swimming", "triathlon"] },
  { id: "waves", glyph: "🌊", sports: ["aquatics", "open water"], keywords: ["water", "sea"] },
  { id: "surfing", glyph: "🏄", sports: ["surfing"] },
  { id: "rowing", glyph: "🚣", sports: ["rowing", "canoeing", "kayaking"], keywords: ["crew"] },
  { id: "sailing", glyph: "⛵", sports: ["sailing", "yachting"], keywords: ["regatta"] },
  { id: "water-polo", glyph: "🤽", sports: ["water polo", "diving"] },

  // --- winter ---
  { id: "skiing", glyph: "⛷️", sports: ["skiing", "alpine", "cross country"] },
  { id: "snowboarding", glyph: "🏂", sports: ["snowboarding"] },
  { id: "ice-skating", glyph: "⛸️", sports: ["figure skating", "ice skating", "curling"] },
  { id: "sledding", glyph: "🛷", sports: ["bobsleigh", "luge", "skeleton"] },

  // --- strength, mind and other ---
  { id: "weightlifting", glyph: "🏋️", sports: ["weightlifting", "powerlifting", "strongman"] },
  { id: "gymnastics", glyph: "🤸", sports: ["gymnastics", "tumbling", "cheerleading"] },
  { id: "climbing", glyph: "🧗", sports: ["climbing", "bouldering"] },
  { id: "yoga", glyph: "🧘", sports: ["yoga", "pilates"] },
  { id: "chess", glyph: "♟️", sports: ["chess"], keywords: ["pawn", "mind sport"] },
  { id: "cards", glyph: "🃏", sports: ["poker", "bridge", "card games"] },
  { id: "board-games", glyph: "🎲", sports: ["board games", "backgammon"] },
  { id: "esports", glyph: "🎮", sports: ["esports", "gaming"] },
  { id: "archery", glyph: "🏹", sports: ["archery"] },
  { id: "angling", glyph: "🎣", sports: ["angling", "fishing"] },

  // --- generic ---
  { id: "trophy", glyph: "🏆", sports: ["any sport"], keywords: ["cup", "champion", "league"] },
  { id: "medal", glyph: "🥇", sports: ["any sport"], keywords: ["gold", "podium", "winner"] },
  { id: "stopwatch", glyph: "⏱️", sports: ["timed events"], keywords: ["time trial", "speed"] },
  { id: "stadium", glyph: "🏟️", sports: ["any sport"], keywords: ["arena", "ground", "venue"] },
];

const BY_ID = new Map(SPORT_EMOJI.map((emoji) => [emoji.id, emoji]));

/** Shown when a sport has not chosen one. */
export const FALLBACK_ICON = "trophy";

export function sportEmoji(id: string | null | undefined): SportEmoji {
  return BY_ID.get(id ?? "") ?? BY_ID.get(FALLBACK_ICON)!;
}

/** The sport this emoji stands for, for the visible tag. */
export function emojiTag(emoji: SportEmoji): string {
  return emoji.sports[0];
}

/** Matches for a search, best first. */
export function searchSportEmoji(query: string): SportEmoji[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return SPORT_EMOJI;
  const scored = SPORT_EMOJI.map((emoji) => {
    const sports = emoji.sports.map((s) => s.toLowerCase());
    if (sports.includes(needle)) return { emoji, score: 0 };
    if (sports.some((s) => s.startsWith(needle))) return { emoji, score: 1 };
    if (sports.some((s) => s.includes(needle))) return { emoji, score: 2 };
    const words = emoji.keywords ?? [];
    if (words.some((k) => k.startsWith(needle))) return { emoji, score: 3 };
    if (words.some((k) => k.includes(needle))) return { emoji, score: 4 };
    if (emoji.id.includes(needle)) return { emoji, score: 5 };
    return { emoji, score: Number.POSITIVE_INFINITY };
  });
  return scored
    .filter((s) => s.score !== Number.POSITIVE_INFINITY)
    .sort((a, b) => a.score - b.score)
    .map((s) => s.emoji);
}

/** Render a sport's emoji by its stored id. */
export function SportGlyph({ id, size = 18 }: { id: string | null | undefined; size?: number }) {
  const emoji = sportEmoji(id);
  return (
    <span
      className="sportEmoji"
      style={{ fontSize: size, lineHeight: 1 }}
      role="img"
      aria-label={emojiTag(emoji)}
    >
      {emoji.glyph}
    </span>
  );
}
